import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { DropdownSelectComponent } from '../../../../shared/components/dropdown-select/dropdown-select.component';
import { LookupService } from '../../../../services/lookup.service';
import {
  FormGroup,
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { NzFormModule } from 'ng-zorro-antd/form';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { ClaimAdjustmentService } from '../../../../services/claim-adjustment.service';
import { ListTableComponent } from '../../../../shared/components/list-table/list-table.component';
import { TimesheetService } from '../../../../services/timesheet.service';
import { ListService } from '../../../../shared/services/list.service';
import {
  DialogConfig,
  DialogService,
} from '../../../../shared/services/dialog.service';
import { ViewClaimTimesheetsComponent } from '../view-claim-timesheets/view-claim-timesheets.component';

@Component({
  selector: 'ppl-adjust-service-claim',
  standalone: true,
  imports: [
    DropdownSelectComponent,
    FormsModule,
    ReactiveFormsModule,
    NzFormModule,
    ButtonComponent,
    ListTableComponent,
    ViewClaimTimesheetsComponent,
  ],
  templateUrl: './adjust-service-claim.component.html',
  styleUrl: './adjust-service-claim.component.css',
})
export class AdjustServiceClaimComponent implements OnInit {
  @Input() claimDetails: any;
  @Input() programName: any;
  @Output() cancel = new EventEmitter<any>();
  @ViewChild('claimTimesheetDetailsDialog')
  claimTimesheetDetailsDialog!: TemplateRef<any>;
  @ViewChild('adjustResultsDialog')
  adjustResultsDialog!: TemplateRef<any>;
  @Input() adjustType = 'service';
  private dialogSvc = inject(DialogService);

  serviceCodes: any;
  adjustClaimForm!: FormGroup;
  mode = 'edit';
  adjustResults: any;
  selectedServiceCode: any;
  claimHistoryData: any;
  authList: any;
  mcoEntries: any;
  claimTSData: any;
  errorCode: any;
  showError = false;
  errorMessage = '';

  cols = [
    {
      field: 'runId',
      header: 'Run ID',
      jsonpath: 'runId',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'createdDate',
      header: 'Created Date',
      jsonpath: 'createdDate',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'status',
      header: 'Status',
      jsonpath: 'status',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
      isClickable: true,
    },
  ];

  constructor(
    private lookupService: LookupService,
    private fb: FormBuilder,
    private claimAdjService: ClaimAdjustmentService,
    private timesheetService: TimesheetService,
    private listService: ListService
  ) {}

  ngOnInit(): void {
    this.adjustClaimForm = this.fb.group({
      serviceCodeDetailId: [null],
      authId: [null],
      mcoId: [null],
    });

    this.getHistoryData();
    if (this.adjustType === 'service') {
      this.getServiceCodes();
      this.cols.splice(
        2,
        0,
        {
          field: 'currentServiceDesc',
          header: 'Service Code',
          jsonpath: 'currentServiceDesc',
          isDisplayedAtHeader: 'y',
          isSearchable: 'n',
        },
        {
          field: 'newservicedesc',
          header: 'New Service Code',
          jsonpath: 'newservicedesc',
          isDisplayedAtHeader: 'y',
          isSearchable: 'n',
        }
      );
    } else {
      this.getMcoEntries();
      this.cols.splice(
        2,
        0,
        {
          field: 'currentMcoName',
          header: 'MCO ',
          jsonpath: 'currentMcoName',
          isDisplayedAtHeader: 'y',
          isSearchable: 'n',
        },
        {
          field: 'newMcoName',
          header: 'New MCO',
          jsonpath: 'newMcoName',
          isDisplayedAtHeader: 'y',
          isSearchable: 'n',
        },
        {
          field: 'currentAuthNumber',
          header: 'Auth',
          jsonpath: 'currentAuthNumber',
          isDisplayedAtHeader: 'y',
          isSearchable: 'n',
        },
        {
          field: 'newAuthNumber',
          header: 'New Auth',
          jsonpath: 'newAuthNumber',
          isDisplayedAtHeader: 'y',
          isSearchable: 'n',
        }
      );
    }

    this.adjustClaimForm
      .get('mcoId')
      ?.valueChanges.subscribe((selectedMcoId) => {
        if (selectedMcoId) {
          this.getAuth(selectedMcoId);
        }
      });
  }

  getMcoEntries() {
    this.lookupService.getOrgData(this.programName).subscribe((data: any) => {
      if (data.status == 200) {
        this.mcoEntries = data.responsedata.filter(
          (item: any) => item.orgTypeLookup === 'MCO'
        );
      }
    });
  }

  openClaimTSDetailsDialog() {
    const dialogConfig: DialogConfig = {
      title: 'Adjustment Details',
      size: 'large',
      position: 'top',
      showClose: true,
      showButtons: false,
    };
    this.dialogSvc.openDialog(
      this.claimTimesheetDetailsDialog,
      dialogConfig,
      () => this.handleCancel(),
      () => this.handleCancel()
    );
  }

  openAdjustResultsDialog() {
    const dialogConfig: DialogConfig = {
      title: 'Result',
      size: 'medium',
      position: 'top',
      showClose: true,
      showButtons: false,
      styleClass: 'split-auth-data',
    };
    this.dialogSvc.openDialog(
      this.adjustResultsDialog,
      dialogConfig,
      () => this.handleCancel(),
      () => this.handleCancel()
    );
  }

  getAuth(id: any) {
    this.claimAdjService
      .getServiceCodesAndAuth(
        this.programName,
        this.claimDetails.participantId,
        id
      )
      .subscribe((data) => {
        this.authList = data.responsedata.map((item: any) => ({
          authId: item.authorizationDetailId,
          value: `${item.authorizationDetailId} - ${item.clientAuthNumber} - ${item.serviceCodeDesc}`,
        }));
      });
  }

  handleCancel() {}

  getServiceCodes() {
    this.claimAdjService
      .getServiceCodesAndAuth(this.programName, this.claimDetails.participantId)
      .subscribe((data: any) => {
        this.serviceCodes = data.responsedata.filter(
          (item: any, index: any, self: any) =>
            index ===
            self.findIndex(
              (t: any) => t.serviceCodeDesc === item.serviceCodeDesc
            )
        );
      });
  }

  onCancel() {
    this.cancel.emit('');
  }

  onAdjust() {
    this.showError = false;
    const payload: any = {
      programCode: this.programName,
      batchClaimDetailId: this.claimDetails?.batchClaimDetailId,
    };

    if (this.adjustType === 'service') {
      if(this.adjustClaimForm.value.serviceCodeDetailId){
        payload.serviceCodeDetailId =
        this.adjustClaimForm.value.serviceCodeDetailId;
      payload.adjustmentType = 'SINGLE_ADJUST';
      }
      else {
        this.showError = true;
        this.errorMessage = 'Please select a service code to adjust.';
        return;
      }
      
    }

    if (this.adjustType === 'mco') {
      if(this.adjustClaimForm.value.mcoId && this.adjustClaimForm.value.authId){
        payload.adjustmentType = 'MCO_ADJUST';
        if (this.adjustClaimForm.value.mcoId) {
          const newMcoName = this.mcoEntries.filter((mco: any) => {
            return mco.orgId === this.adjustClaimForm.value.mcoId;
          });
          payload.newMcoName = newMcoName[0].orgName;
        }
        if (this.adjustClaimForm.value.authId) {
          payload.authorizationDetailId = this.adjustClaimForm.value.authId;
        }
      }
      else {
        this.showError = true;
        this.errorMessage = 'Please select an MCO and an Auth Number to adjust.';
        return;
      }
    }

    this.claimAdjService
      .adjustClaims(payload, this.adjustType)
      .subscribe((data: any) => {
        if (data.status === 200) {
          this.mode = 'read';
          this.adjustClaimForm.reset();
          this.adjustResults = data?.responsedata?.ReturnMessage;
          this.openAdjustResultsDialog();
          this.getHistoryData();
        }
      });
  }

  buildRequestPayload(
    pageName: string,
    orderBy: string,
    searchParams: any,
    startingPageNumber = 1
  ) {
    const payload: any = {
      pageName: pageName,
      programCode: this.programName,
      condition: 'and',
      recordsPerPage: 20,
      startingPageNumber: startingPageNumber,
      searchParam: searchParams,
      orderByColumn: orderBy ? [orderBy] : [],
      orderBy: ['desc'],
    };
    return payload;
  }

  getHistoryData() {
    const filters = [
      {
        jsonPath: 'pplIcn',
        value: this.claimDetails.pplIcn,
        operator: 'Equal',
      },
    ];

    const pageName =
      this.adjustType === 'mco'
        ? 'billingMCOAdjustment'
        : 'billingServiceAdjustment';
    const processedPayload = this.buildRequestPayload(
      pageName,
      'createdDate',
      filters
    );
    this.listService
      .getListData(processedPayload, 'claimadj')
      .subscribe((data: any) => {
        this.claimHistoryData = data.responsedata;
      });
  }

  rowActions(event: any) {
    if (event?.col?.field === 'status') {
      const id = event?.event?.id;
      const detailsData = this.claimHistoryData.filter(
        (data: any) => data.id === id
      );
      const claim = detailsData[0];
      this.claimTSData = claim?.currentTimesheets?.map(
        (id: number, index: number) => ({
          id: id,
          status: claim?.newTimesheets[index],
        })
      );
      this.errorCode = claim?.errCode;
      this.openClaimTSDetailsDialog();
    }
  }

  formatDate(dateStr: string): string {
    const [month, day, year] = dateStr.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
}
